import React from 'react';
import { useIntl } from 'react-intl';
import { AssetNodeType } from '../models';
import { Button } from 'reactstrap';
import AddComponentDropdown from './AddComponentDropdown';
import UserGroupPermissions, {
  archivePermissionRefs,
  editPermissionRefs,
} from '../../../core/components/UserGroupPermissions';

// Colors live in _assets.scss as asset-detail-header__node-indicator--<type>.
const indicatorTypes: string[] = [
  AssetNodeType.Template,
  AssetNodeType.Collection,
  AssetNodeType.Group,
  AssetNodeType.Item,
];

interface BreadcrumbItem {
  id: string;
  name: string;
  type: string;
}

interface AssetDetailHeaderProps {
  node: any;
  breadcrumb: BreadcrumbItem[];
  onAddNew: () => void;
  onAddTemplate: () => void;
  onAddGroup: () => void;
  onAddAsset: () => void;
  onEditGroup?: (group: any) => void;
  onEditAsset?: (item: any) => void;
  onEditTemplate?: (template: any) => void;
  onArchiveNode?: (node: any) => void;
  onUnarchiveNode?: (node: any) => void;
  /** Add Component shapes the blueprint, not a site deployment. */
  showStructureActions?: boolean;
  /** Deployment context: the template photo and the area it sits in. */
  imageUrl?: string | null;
  areaLabel?: string | null;
  deployedAt?: string | null;
}

const AssetDetailHeader: React.FC<AssetDetailHeaderProps> = ({
  node,
  breadcrumb,
  onAddNew,
  onAddTemplate,
  onAddGroup,
  onAddAsset,
  onEditGroup,
  onEditAsset,
  onEditTemplate,
  onArchiveNode,
  onUnarchiveNode,
  showStructureActions = true,
  imageUrl,
  areaLabel,
  deployedAt,
}) => {
  const intl = useIntl();

  const indicatorType = !node
    ? AssetNodeType.Template
    : indicatorTypes.includes(node.type)
    ? node.type
    : AssetNodeType.Item;
  // Ids are spelled out per branch: intl extraction needs static ids.
  const nodeTypeName = !node
    ? ''
    : node.type === AssetNodeType.Group
    ? intl.formatMessage({ id: 'assets.type.group' })
    : node.type === AssetNodeType.Item
    ? intl.formatMessage({ id: 'assets.type.item' })
    : node.type === AssetNodeType.Template
    ? intl.formatMessage({ id: 'assets.type.template' })
    : node.type === AssetNodeType.Collection
    ? intl.formatMessage({ id: 'assets.type.collection' })
    : node.type;

  const isArchived = Boolean((node?.raw || node)?.archivedAt);

  const editHandlerByType: Record<string, ((raw: any) => void) | undefined> = {
    [AssetNodeType.Group]: onEditGroup,
    [AssetNodeType.Item]: onEditAsset,
    [AssetNodeType.Template]: onEditTemplate,
  };
  const onEdit = node ? editHandlerByType[node.type] : undefined;

  const addComponent = (
    <AddComponentDropdown
      onAddTemplate={onAddTemplate}
      onAddGroup={onAddGroup}
      onAddAsset={onAddAsset}
      onAddTask={node ? onAddNew : undefined}
    />
  );

  return (
    <div className="asset-detail-header">
      {!node && showStructureActions && (
        <div className="asset-detail-header__node-actions">{addComponent}</div>
      )}

      {node && (
        <div className="asset-detail-header__node-info">
          {imageUrl !== undefined ? (
            <span className="asset-detail-header__image">
              {imageUrl ? <img src={imageUrl} alt="" /> : <i className="uil uil-image" />}
            </span>
          ) : (
            <span
              className={`asset-detail-header__node-indicator asset-detail-header__node-indicator--${indicatorType}`}
            />
          )}
          <div className="asset-detail-header__node-details">
            <span className="asset-detail-header__node-type">{nodeTypeName.toUpperCase()}</span>
            <h2 className="asset-detail-header__node-name">
              {node.name}
              {isArchived && (
                <span className="badge badge-secondary ml-2 align-middle">
                  {intl.formatMessage({ id: 'assets.archive.archivedBadge' })}
                </span>
              )}
            </h2>
            {(deployedAt || areaLabel) && (
              <p className="asset-detail-header__node-meta">
                {deployedAt && <span>{new Date(deployedAt).toLocaleDateString()}</span>}
                {areaLabel && (
                  <span className="asset-detail-header__node-area">
                    <i className="uil uil-layer-group" />
                    {areaLabel}
                  </span>
                )}
              </p>
            )}

            {node.type === AssetNodeType.Item && (
              <p className="asset-detail-header__node-description">
                {intl.formatMessage(
                  { id: 'assets.detail.partOfGroup' },
                  {
                    group: breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2].name : '',
                    collection: breadcrumb.length > 2 ? breadcrumb[breadcrumb.length - 3].name : '',
                  }
                )}
              </p>
            )}
          </div>

          <div className="asset-detail-header__node-actions">
            {onEdit && (
              <UserGroupPermissions itemRef={editPermissionRefs.assets}>
                <Button outline color="primary" size="sm" onClick={() => onEdit(node.raw || node)}>
                  <i className="uil uil-edit mr-1" />
                  {intl.formatMessage({ id: 'assets.actions.edit' })}
                </Button>
              </UserGroupPermissions>
            )}
            {!isArchived && onArchiveNode && (
              <UserGroupPermissions itemRef={archivePermissionRefs.assets}>
                <Button outline color="secondary" size="sm" onClick={() => onArchiveNode(node)}>
                  <i className="uil uil-archive mr-1" />
                  {intl.formatMessage({ id: 'assets.actions.archive' })}
                </Button>
              </UserGroupPermissions>
            )}
            {isArchived && onUnarchiveNode && (
              <UserGroupPermissions itemRef={archivePermissionRefs.assets}>
                <Button outline color="primary" size="sm" onClick={() => onUnarchiveNode(node)}>
                  <i className="uil uil-history mr-1" />
                  {intl.formatMessage({ id: 'assets.actions.unarchive' })}
                </Button>
              </UserGroupPermissions>
            )}
            {showStructureActions && addComponent}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetDetailHeader;
